<template>
  <div class="intro">
    <div>
      <!-- Golem fork: the walkthrough paragraph is three themed doors. Each
           button drives the SAME path as its hotkey (a synthetic keyup), so
           there is exactly one host/join/create flow to maintain. -->
      <!-- In a session with no seats yet, the next step is SEATS, not doors:
           the host adds them. (FT-852: the player waiting screen is retired —
           App only renders Intro when sessionless; a player in a session
           always sees the live town square.) -->
      <template v-if="session.sessionId && !session.isSpectator">
        <p class="hint">Hosting <b>{{ session.sessionId }}</b> — add seats to build the town.</p>
        <ul class="doors">
          <li @click="press('a')">
            <span class="key"
              ><img :src="capSrc('A')" :class="capClass('a')" :style="capStyle('A')" alt="A"
            /></span>dd Players
          </li>
        </ul>
      </template>
      <template v-else>
        <!-- Golem fork: the page TITLE — "BLOOD" in the blood alphabet, a
             quiet "on the" beneath it; the dial's CLOCKTOWER letters in the
             art complete the name. DOM overlay (not baked into the
             background), pinned to the sky band above the clock face. Static
             PNGs — nothing animates, so the kill-switch has nothing to kill. -->
        <div
          class="title"
          aria-label="Lights Over Threnody's Shadows"
          :style="titleStyle"
        >
          <!-- Golem fork: clicking the word cycles its lettering — 0 the
               blood-alphabet PNGs, 1 the official-style gold logo art
               (extracted from the source header, FT-853). Persisted so the
               choice survives reloads; fixed row height so nothing below
               shifts. -->
          <!-- the DESIGNED Blood lockup, lifted whole from the user's
               blood-on-the.png (2026-08-18) — flourishes and all -->
          <div class="blood-word">
            <img class="blood-lockup" :src="bloodLockup" alt="Blood" />
          </div>
          <!-- "on the": the gold script art, or any family's lowercase
               (the font lab picks) -->
        </div>
        <!-- "on the" anchors on its OWN image point — the rosette ring's
             centre (822, 147), which sits left of the title axis -->
        <div class="on-the" :style="ontheStyle">
          <!-- the DESIGNED script lifted whole from the 800000 logo lockup
               (user call 2026-08-18) — swash rule + "on the" as one piece -->
          <img class="onthe-lockup" :src="ontheLockup" alt="on the" />
        </div>
        <ul class="doors" v-if="!mode && !waitingTown && !keyGateTown">
          <li @click="openHost">
            <span class="key"
              ><img :src="capSrc('H')" :class="capClass('h')" :style="capStyle('H')" alt="H"
            /></span>ost
          </li>
          <li @click="openJoin">
            <span class="key"
              ><img :src="capSrc('J')" :class="capClass('j')" :style="capStyle('J')" alt="J"
            /></span>oin
          </li>
          <li @click="openCreate">
            <span class="key"
              ><img :src="capSrc('S')" :class="capClass('s')" :style="capStyle('S')" alt="S"
            /></span>cripts
          </li>
          <!-- FT-1146: THE RECORDS DOOR. The town records have only ever been
               reachable from inside a town — the strip that carried them
               renders `v-if="inGame"` — which is exactly backwards for a
               surface whose whole subject is EVERY town. It is a front door
               beside Host and Join because that is what it is: a thing you
               come to the site to look at, not a panel you raise mid-game. -->
          <!-- FT-1159 (user call, 2026-08-25): "The record button on the home
               shouldn't be in the main list of things a user does, maybe we
               put it in the top right as the chronicles button as well." Host,
               Join and Scripts are the things a person is CHOOSING BETWEEN
               when they arrive — Records is not one of them, and standing it
               fourth in that stack said it was. The destination is unchanged;
               it wears a corner mark now instead of a door, in the in-game
               strip's own box (Menu.vue's `.entry-strip`) — the same corner,
               the same 26px art, the same plate. STOOD DOWN, not removed
               (house rule): the markup, its handler, the blood-R import and
               the `.blood-cap-r` sizing all stay in the tree. -->
          <li v-if="false" @click="openRecords">
            <span class="key"
              ><img
                :src="capSrc('R')"
                :class="capClass('r')"
                :style="capStyle('R')"
                alt="R" /></span
            >ecords
          </li>
        </ul>

        <!-- Golem fork: Host and Join swap the doors for in-app panels —
             no browser dialogs anywhere in the entry flow. -->
        <!-- FT-887: both panels are DISCS on the desktop clock face now, the
             same object NightSheet's checklist already is (see the style
             block's disc media query) — a phone/small window still gets the
             original rectangle, untouched. The disc's two caps are furniture
             a circle can actually hold: the back arrow (+ the join panel's
             one-line hint) up top, the primary button at the foot. Everything
             else — the town list and the fields — is `.panel-body`, the
             band inscribed between them. -->
        <!-- 2026-08-19: NOT OPEN YET. Someone followed an invite link, or
             typed a town name, before its storyteller opened it. That is a
             normal thing to do — early is not wrong — so this is a WAIT, not
             a refusal: the gate keeps polling and the player is carried in
             the moment a storyteller appears. It is the same panel object
             (disc on the desktop face, rectangle on a phone) the Host and
             Join panels are, so it inherits their geometry rather than
             inventing a fourth surface. It outranks `mode` because a wait can
             begin with no panel open at all. -->
        <!-- FT-1241: THE TOWN'S DOOR ASKS FOR A PASSWORD. Every player entry
             path funnels through the same gate (enterWhenOpen), so this one
             panel serves the Join button and a followed invite link alike —
             the link itself never carries the secret; this ask is how it
             arrives. Same panel object as the wait screen (disc on the
             desktop face, rectangle on a phone), because it IS the same
             moment: standing at a town, not yet inside. It outranks the wait
             — the key is asked for first, the waiting starts after. -->
        <div class="panel wait" v-else-if="keyGateTown">
          <div class="panel-head">
            <span class="panel-back" title="Back" @click="stopKeyGate"
              ><font-awesome-icon icon="arrow-left"
            /></span>
            <p class="hint">Password, please</p>
          </div>
          <div class="panel-body">
            <p class="wait-town">{{ keyGateLabel }}</p>
            <p class="wait-why">
              {{
                enterKeyGate.error
                  ? "That's not this town's password — ask your storyteller."
                  : "This town asks everyone entering for a password."
              }}
            </p>
            <div class="field">
              <label title="The town's password"
                ><font-awesome-icon icon="key"
              /></label>
              <input
                ref="enterPassInput"
                v-model="enterPassTry"
                spellcheck="false"
                autocomplete="off"
                placeholder="password"
                @keyup.enter="offerPass"
              />
            </div>
          </div>
          <div class="acts">
            <button
              class="confirm"
              :class="{ disabled: !enterPassTry.trim() }"
              title="Enter the town"
              @click="offerPass"
            >
              Enter the town
            </button>
          </div>
        </div>

        <div class="panel wait" v-else-if="waitingTown">
          <div class="panel-head">
            <p class="hint">Not open yet</p>
          </div>
          <div class="panel-body">
            <p class="wait-town">{{ waitingLabel }}</p>
            <p class="wait-why">
              The storyteller hasn't opened this town yet.
            </p>
            <p class="wait-what">
              Stay here and you'll walk in the moment they do.
            </p>
            <div class="wait-pulse" aria-hidden="true">
              <span></span><span></span><span></span>
            </div>
          </div>
          <div class="acts">
            <button
              class="confirm"
              title="Go back to the entry screen"
              @click="stopWaiting"
            >
              Stop waiting
            </button>
          </div>
        </div>

        <div class="panel" v-else-if="mode === 'host'">
          <div class="panel-head">
            <span class="panel-back" title="Back" @click="mode = null"
              ><font-awesome-icon icon="arrow-left"
            /></span>
          </div>
          <div class="panel-body">
          <ul class="towns" v-if="hostTowns.length">
            <li
              v-for="t in hostTowns"
              :key="t.id"
              :class="{ picked: townId === t.id }"
              @click="townId = t.id"
            >
              <span class="dot" :class="dotClass(t.id)"></span>
              <span class="name" :title="t.id">
                {{ townLabel(t) }}
                <small class="tid" v-if="townLabel(t) !== t.id">{{ t.id }}</small>
              </span>
              <small class="yours" v-if="isYours(t)">yours</small>
              <small>{{ statusLabel(t.id) }}</small>
            </li>
          </ul>
          <div class="field">
            <!-- FT-1242: FA `home` stood down — the town's own mark
                 (ui-town.png, the count strip's town icon). -->
            <label title="Town name"
              ><img class="field-mark" :src="uiTown" alt="Town name"
            /></label>
            <div class="town-input">
              <input
                ref="townInput"
                v-model="townId"
                spellcheck="false"
                autocomplete="off"
                @input="onTownInput"
                @focus="onTownFocus"
                @blur="onTownBlur"
                @keydown.esc="townDropdownOpen = false"
                @keyup.enter="confirmHost"
              />
              <span
                class="caret"
                :class="{ open: townDropdownOpen }"
                title="Show remembered towns"
                @mousedown.prevent="openTownDropdown"
              >
                <font-awesome-icon icon="chevron-down" />
              </span>
              <ul class="recents" v-if="townDropdownOpen">
                <li v-for="t in recentTowns" :key="t.id">
                  <span
                    class="name"
                    :title="t.id"
                    @mousedown.prevent="pickRecent(t)"
                  >
                    {{ townLabel(t) }}
                    <small class="tid" v-if="townLabel(t) !== t.id">{{ t.id }}</small>
                  </span>
                  <small class="yours" v-if="isYours(t)">yours</small>
                  <span
                    class="forget"
                    :class="{ sure: forgetConfirm === t.id }"
                    :title="t.editKey ? 'Forget this town (click again to confirm — you\'ll lose its edit key)' : 'Forget this town'"
                    @mousedown.prevent="forgetRecent(t)"
                    >{{ forgetConfirm === t.id ? "sure?" : "×" }}</span
                  >
                </li>
                <li class="new-town" @mousedown.prevent="pickNewTown">
                  <span class="name">+ New town</span>
                </li>
              </ul>
            </div>
            <span class="tool" @click="reroll" title="Roll a new name">
              <font-awesome-icon icon="random" />
            </span>
          </div>
          <!-- Golem fork (FT-854): THE shared ScriptPicker — the identical
               component the Almanac workbench renders. Change it there,
               change it here. -->
          <div class="field">
            <!-- FT-1242: FA `scroll` stood down — the app's own script
                 scroll (ui-script.png, the strip and host panel's mark). -->
            <label title="Script"
              ><img
                class="field-mark field-mark-dark"
                :src="uiScript"
                alt="Script"
            /></label>
            <ScriptPicker
              :cards="scriptCards"
              :picked-id="scriptId"
              @pick="pickScript"
              @open="peekVaultMeta"
            />
          </div>
          <!-- FT-1012 (user call, 2026-08-20): the owned-town note is GONE
               ("I think we can remove that message right?") — ownership still
               claims and saves exactly as FT-847 built it; the panel just
               stops saying so. -->

            <!-- FT-1241: the town's optional locks, set at claim. The ENTER
                 password is the room key players must give; the OPEN password
                 only appears for a signed-OUT host — signed in, the account
                 itself is how the host seat re-opens, so the field would be
                 noise. Neither renders for a town this browser already holds
                 (edit key / owned account): changing an existing town's locks
                 is a host-tools job, not a door-side one. -->
            <div class="field" v-if="!townIsMine && !hostRefusal">
              <label
                title="Ask everyone entering this town for a password (optional)"
                ><font-awesome-icon icon="key"
              /></label>
              <input
                v-model="hostEnterPass"
                spellcheck="false"
                autocomplete="off"
                placeholder="password to enter (optional)"
                @keyup.enter="confirmHost"
              />
            </div>
            <div class="field" v-if="!townIsMine && !hostRefusal && !account">
              <label
                title="Anyone knowing this password can open the town as its host — your way back in from another browser (optional)"
                ><font-awesome-icon icon="lock"
              /></label>
              <input
                v-model="hostOpenPass"
                spellcheck="false"
                autocomplete="off"
                placeholder="password to host (optional)"
                @keyup.enter="confirmHost"
              />
            </div>
            <!-- FT-1241: THE REFUSAL, on the glass. The platform said no to the
                 host seat; this says exactly why, in the server's own honest
                 vocabulary, and offers the one thing that could change the
                 answer — the open password field when one exists, the sign-in
                 direction when the town belongs to an account. -->
            <div class="host-refusal" v-if="hostRefusal">
              <p class="refusal-why">{{ hostRefusalText }}</p>
              <div class="field" v-if="hostRefusal.openPasswordSet">
                <label title="This town's open password"
                  ><font-awesome-icon icon="key"
                /></label>
                <input
                  ref="openTryInput"
                  v-model="hostOpenTry"
                  spellcheck="false"
                  autocomplete="off"
                  placeholder="open password"
                  @keyup.enter="confirmHost"
                />
              </div>
            </div>

          <div class="field">
            <!-- Golem fork: the chain glyph is a copy button too — the URL
                 span beside it already copies, and a glyph that names the
                 link but ignores a click reads as broken. Same handler, same
                 copied-tick feedback. -->
            <label
              class="link-copy"
              :title="copied ? 'Copied!' : 'Copy the invite link'"
              @click="copyShare"
              ><font-awesome-icon :icon="copied ? 'check' : 'link'"
            /></label>
            <span
              class="share"
              @click="copyShare"
              :title="copied ? 'Copied!' : 'Copy the invite link'"
            >
              <span class="url">{{ shareLink }}</span>
              <font-awesome-icon :icon="copied ? 'check' : 'copy'" />
            </span>
          </div>
          </div>
          <div class="acts">
            <!-- Golem fork (FT-854): the primary action wears WORDS in the
                 door idiom — blood-O drop cap + label. The blood letter is
                 the hotkey promise: O opens the town while this panel is up. -->
            <button
              class="confirm open-town"
              :class="{ disabled: !townIdClean }"
              title="Open the town (O)"
              @click="confirmHost"
            >
              <span class="key"
                ><img :src="capSrc('O')" :class="capClass('o')" :style="capStyle('O', 1)" alt="O"
              /></span>pen the town
            </button>
          </div>
        </div>

        <div class="panel" v-else>
          <div class="panel-head">
            <span class="panel-back" title="Back" @click="mode = null"
              ><font-awesome-icon icon="arrow-left"
            /></span>
            <p class="hint">Enter a Town</p>
          </div>
          <div class="panel-body join-body">
          <!-- FT-1038 (user call): "shouldn't this have a place for recent
               towns? list the 3 most recent the user has been in" — any
               role, since a town hosted last week is as worth a one-click
               return to as one joined. A row here COMMITS (enterRecent goes
               straight to confirmJoin when a name is already on file)
               rather than just filling the field the way the Host panel's
               `ul.towns` picker does, so it stays its own list. -->
          <ul class="recent-shelf" v-if="recentEnterTowns.length">
            <li
              v-for="t in recentEnterTowns"
              :key="t.id"
              @click="enterRecent(t)"
            >
              <span class="dot" :class="dotClass(t.id)"></span>
              <span class="name" :title="t.id">{{ townLabel(t) }}</span>
              <small>{{ statusLabel(t.id) }}</small>
            </li>
          </ul>
          <div class="field">
            <!-- FT-1242: FA `home` stood down — the town's own mark
                 (ui-town.png, the count strip's town icon). -->
            <label title="Town name"
              ><img class="field-mark" :src="uiTown" alt="Town name"
            /></label>
            <input
              v-model="joinId"
              spellcheck="false"
              placeholder="town name or link"
              @keyup.enter="confirmJoin"
            />
          </div>
          <div class="field">
            <label title="Your name"><font-awesome-icon icon="user" /></label>
            <input
              ref="joinNameInput"
              v-model="joinName"
              spellcheck="false"
              placeholder="what the town calls you"
              @keyup.enter="confirmJoin"
            />
          </div>
          <!-- FT-1038: the door leaves the foot cap (it used to ride `.acts`
               down at the rim, small — see the CSS note where that lived)
               and stands instead at the band's own centre, which is also
               the disc's (see .join-body's style comment). `.enter-dock`
               just claims whatever room the shelf and fields above leave. -->
          <div class="enter-dock">
            <button
              class="confirm enter-door"
              :class="{ disabled: !canJoin }"
              title="Enter the town"
              @click="confirmJoin"
            >
              <!-- our own mark (user-supplied art, 2026-08-19): a figure on
                   the road to a town whose tallest building is a clocktower.
                   Baked to the same measured material as the strip's marks —
                   silhouette only, no outline, the fork's stone grain — from a
                   source that was white on TRANSPARENT, so its own alpha is
                   the mask rather than anything the pixels' darkness says. -->
              <img class="enter-mark" :src="uiEnter" alt="" />
            </button>
            <!-- FT-1189 (user): "add a label below that". The door was a mark
                 and a `title`, which is a name you only meet by hovering a
                 control you have not decided to press yet — and on a touch
                 screen it is a name that does not exist at all. The word says
                 what the mark means, and dims with the door when there is
                 nothing yet to enter, so the disabled state reads as "not
                 yet" rather than as a picture that ignores you. -->
            <span class="enter-label" :class="{ disabled: !canJoin }"
              >Enter the town</span
            >
          </div>
          </div>
        </div>
      </template>
    </div>
    <!-- the Pandemonium Institute footer credit lives in App.vue — inside the
         intro it was trapped under the town square's layer and took no click -->
    <a
      class="redirect"
      v-if="language === 'zh-CN'"
      href="https://clocktower.gstonegames.com"
    >
      <img src="../assets/gstone.png" class="gstone" alt="" />
      你想使用中文版魔典吗？
    </a>
  </div>
</template>

<script>
import { mapState } from "vuex";
import editionJSON from "../editions.json";
import {
  listTowns,
  townStatuses,
  mintTownId,
  mintAvailableTownId,
  normalizeTownId,
  editKeyFor,
  claimTown,
  townMeta,
  removeTown,
  // 2026-08-19: the closed-town gate — a town nobody has opened is waited
  // for, not entered.
  enterWhenOpen,
  stopWaitingForTown,
  townGate,
  // FT-1241: town ownership + the two optional passwords — the host-seat
  // check and the player-side password gate.
  openTown,
  enterKeyGate,
  offerEnterPass,
  stopEnterPassGate
} from "../golem/towns";
import { resolveTownRole } from "../golem/townRoute";
// FT-1032: the shelf row says WHERE a running game stands. Both reads are
// this browser's own stashes: the deal moment (golem/stats, stamped when
// this browser dealt as host, cleared when its game ends) and the town's
// night log (golem/nightLog, which carries the day counter) — so the row
// costs no relay call and can only ever speak about games this browser runs.
import { dealTimeFor } from "../golem/stats";
import { loadLog } from "../golem/nightLog";
import bloodH from "../assets/blood/blood-H.png";
import bloodJ from "../assets/blood/blood-J.png";
import bloodC from "../assets/blood/blood-C.png";
import bloodA from "../assets/blood/blood-A.png";
// FT-1146: the Records door's own baked cap. (The art has been in the tree
// since FT-846 — the door set was cut whole; only four letters had doors.)
import bloodR from "../assets/blood/blood-R.png";
import bloodMetrics from "../assets/blood/metrics.json";
// Golem fork: the page title's "BLOOD" — the FT-846 blood alphabet archive
// (O2 is the variant, so the double O never repeats a letterform).
// (title letters now come from titleFonts.js — the font-set registry)
// Golem fork (FT-853): the title's alternate lettering — official-style gold
// logo art extracted from the source header (Blood + the small "on the"
// script), keyed to transparent PNGs.
import bloodLogo from "../assets/title/blood-logo.png";
import ontheLogo from "../assets/title/onthe-logo.png";
import ontheLockup from "../assets/title/onthe-lockup.png";
import uiEnter from "../assets/ui-enter.png";
// FT-1242: the entry form's field marks in the app's own art — the town
// (ui-town.png, the count strip's icon) and the script scroll (ui-script.png,
// the top strip / host panel's mark). FA `home`/`scroll` stood down in place.
import uiTown from "../assets/ui-town.png";
import uiScript from "../assets/ui-script.png";
import bloodLockup from "../assets/title/blood-lockup.png";
// FT-854: the Open-the-town button's blood O (alphabet archive; ems baked
// from its metrics at the title conversion — 341x410, baseline 352, /370).
import bloodO from "../assets/blood/alphabet/O.png";
// Golem fork (FT-854): the picker + its art moved to shared homes — the host
// panel and the Almanac workbench render the SAME component and imagery.
import ScriptPicker from "./ScriptPicker";
import { EDITION_ICONS, edCustom, OFFICIAL_BLURBS } from "../golem/editionArt";
import { getRecents, peekScript } from "../golem/scripts";
// the app-wide PNG-font choices (the Aa font lab in App is the control)
import {
  fontState,
  glyph,
  glyphStyle,
  glyphFrom,
  glyphStyleFrom,
  glyphCapStyleFrom,
  resolvedCapKey
} from "../golem/titleFonts";

// Golem fork (FT-846): the door initials are pre-rendered blood letters
// (Creepster + an SVG goo/drip/crust treatment, baked at 2x). Baked PNGs, not
// the live filter, because the filter's radii fall below one device pixel at
// door size and quantize the letterforms apart.
// Metrics are image px against a 180px reference font with the glyph baseline
// at image y=400; the drop-cap displays at 1.09x the key font (the 1.45x
// original, sized down ~25% for the vertical door stack), so ems convert as
// 1.09 / (180 * 2x) = 1.09/360 em per trimmed pixel — the static
// .blood-cap-* sizes in the style block below are that conversion, baked.
const BLOOD = {
  H: { src: bloodH, ...bloodMetrics.H },
  J: { src: bloodJ, ...bloodMetrics.J },
  C: { src: bloodC, ...bloodMetrics.C },
  A: { src: bloodA, ...bloodMetrics.A },
  R: { src: bloodR, ...bloodMetrics.R }
};

// The title's blood glyphs. The alphabet archive is original-artwork scans on
// its own scale ({w, h, baseline} image px); titleFonts.js now owns the
// registry and normalizes every family by its B's cap height.

export default {
  components: { ScriptPicker },
  computed: {
    ...mapState(["session", "edition"]),
    editionList() {
      return editionJSON;
    },
    /** "on the" in the chosen family's lowercase (goldart mode bypasses). */
    /** Title GLUED to the art: positioned + sized in image coordinates
     *  (tower axis x 807 — was x 837 on the original 1672-wide art, shifted
     *  -30 by the recentre trim; the dark band above the arch tops at y 60;
     *  the lettering is 88 image-px tall), so no window size moves it. */
    titleStyle() {
      const { x, y, s } = this.bgA;
      return {
        // FT-1190: + the background's baked 7px shift and the lab's live
        // scrub, exactly as ontheStyle below already reads them. `bgA` maps
        // IMAGE coordinates onto a paint assumed to be centred; the paint is
        // not centred (App.vue publishes `calc(50% + 7px + var(--bg-off-x))`
        // as the face centre), so x 807 alone put the lockup 7px left of the
        // tower axis it is glued to — and 7px left of "on the" beneath it,
        // which does carry the offset. One expression, both halves.
        left: `calc(${x + 807 * s}px + 7px + var(--bg-off-x, 0px))`,
        top: y + 56 * s + "px",
        // the lockup draws at 360 image-px wide (1657x651 native)
        width: 318 * s + "px",
        fontSize: 88 * s + "px"
      };
    },
    /** "on the" — the lockup script, image-anchored (user-calibrated; x 820
     *  was x 850 on the original art, shifted -30 by the recentre trim). */
    ontheStyle() {
      const { x, y, s } = this.bgA;
      return {
        // + the background's baked 7px shift (FT-881). This lockup is anchored
        // to a point in the ART — the rosette ring's centre — so when the paint
        // moved right, it had to move with it or drift off the thing it sits
        // on. Reading the same variable means it tracks any future change to
        // that offset instead of needing to be re-found by hand.
        left: `calc(${x + 820 * s}px + 7px + var(--bg-off-x, 0px))`,
        top: y + 191 * s + "px",
        // the lockup renders at 42 image-px tall (431x98 native)
        height: 36 * s + "px"
      };
    },
    ontheGlyphs() {
      const key = this.fontState.ontheKey;
      const out = [];
      ["o_lc", "n_lc", null, "t_lc", "h_lc", "e_lc"].forEach(letter => {
        if (!letter) {
          out.push({ space: true });
          return;
        }
        const g = glyphFrom(key, letter);
        if (g)
          out.push({
            src: g.src,
            alt: letter[0],
            style: glyphStyleFrom(key, letter, 1)
          });
      });
      return out;
    },
    // Golem fork: the title's BLOOD letter row, from the ACTIVE font set
    // (titleFonts.js normalizes sizes across families by the B's cap height).
    titleGlyphs() {
      if (this.fontState.key === "logo") return [];
      return ["B", "L", "O", "O2", "D"].map(letter => {
        const g = glyph(letter) || {};
        return {
          src: g.src,
          alt: letter[0],
          style: glyphStyle(letter, 1)
        };
      });
    },
    // Golem fork: the script grid's cards — the town's saved script (when
    // owned), the officials in editions.json order, the vault shelf, and the
    // custom/vault door last. Vault blurbs fill in lazily from scriptMeta.
    scriptCards() {
      const cards = [];
      if (this.attachedScriptId) {
        cards.push({
          id: "__attached",
          name: "This town's saved script",
          icon: edCustom,
          blurb: this.vaultBlurb(this.attachedScriptId),
          source: "This town"
        });
      }
      this.editionList.forEach(e => {
        cards.push({
          id: e.id,
          name: e.name,
          icon: EDITION_ICONS[e.id] || edCustom,
          blurb: OFFICIAL_BLURBS[e.id] || "",
          source: "OFFICIAL"
        });
      });
      this.vaultShelf
        .filter(s => s.id !== this.attachedScriptId)
        .forEach(s => {
          const meta = this.scriptMeta[s.id];
          cards.push({
            id: s.id,
            name: s.name || s.id,
            icon: edCustom,
            blurb: this.vaultBlurb(s.id),
            source: meta && meta.author ? `by ${meta.author}` : "Scripts"
          });
        });
      cards.push({
        id: "__custom",
        name: "Scripts…",
        icon: edCustom,
        blurb: "Build your own script, or load any script by link.",
        source: "Scripts"
      });
      return cards;
    },
    townIdClean() {
      return normalizeTownId(this.townId);
    },
    // FT-847: does THIS browser hold the picked town's edit key?
    ownsPicked() {
      return !!(this.townIdClean && editKeyFor(this.townIdClean));
    },
    // The script an OWNED picked town carries in its server meta (if any).
    attachedScriptId() {
      const id = this.townIdClean;
      if (!id || !editKeyFor(id)) return null;
      const m = this.meta[id];
      return (m && m.scriptId) || null;
    },
    shareLink() {
      // Clean path link — no hash. window.location.origin ignores whatever
      // path/hash happens to be open right now (e.g. a joined /<town>).
      return window.location.origin + "/" + this.townIdClean;
    },
    canJoin() {
      return !!(this.joinIdClean && this.joinName.trim());
    },
    /** 2026-08-19: the town being waited on, "" when nothing is. This is the
     *  panel selector — it outranks `mode`, so a wait started from an invite
     *  link (where no panel was ever opened) shows the same screen a wait
     *  started from the Join panel does. */
    waitingTown() {
      return this.townGate.town;
    },
    /** Its display name if the server knows one, else the id the player
     *  followed — never blank, and never the raw channel jargon. */
    waitingLabel() {
      const id = this.waitingTown;
      const m = this.meta[id];
      return (m && m.name) || id;
    },
    /** FT-1241: the signed-in account, or null — the towns API records it as
     *  owner on claim, so the panel's open-password field is guests-only. */
    account() {
      return this.session.account || null;
    },
    /** FT-1241: the town in the host field is already this browser's — an
     *  edit key on the shelf, or the account owns it (server meta). The
     *  lock fields hide for it: its locks are set, not being chosen. */
    townIsMine() {
      const id = this.townIdClean;
      if (!id) return false;
      if (editKeyFor(id)) return true;
      const m = this.meta[id];
      return !!(m && m.ownedByYou);
    },
    /** FT-1241: the town whose door is asking for a password ("" = none).
     *  A panel selector like waitingTown — it outranks mode AND the wait,
     *  because the key is asked before the waiting starts. */
    keyGateTown() {
      return this.enterKeyGate.town;
    },
    keyGateLabel() {
      const id = this.keyGateTown;
      const m = this.meta[id];
      return (m && m.name) || id;
    },
    /** FT-1241: the refusal, worded from the server's honest booleans. */
    hostRefusalText() {
      const r = this.hostRefusal;
      if (!r) return "";
      if (r.error === "wrong_password") {
        return "That's not this town's open password.";
      }
      if (r.owned && r.openPasswordSet) {
        return "This town belongs to an account. Sign in as its owner, or give its open password.";
      }
      if (r.owned) {
        return "This town belongs to an account — sign in as its owner to host it.";
      }
      return "This town is protected — its open password takes the host seat.";
    },
    joinIdClean() {
      let raw = this.joinId.trim();
      if (raw.match(/^https?:\/\//i)) {
        const hashAt = raw.indexOf("#");
        raw =
          hashAt >= 0
            ? raw.slice(hashAt + 1)
            : raw.replace(/^https?:\/\/[^/]+\/?/i, "").split(/[/?]/)[0];
      }
      return normalizeTownId(raw);
    },
    // Shelf entries offered under the Town field: newest first (either
    // role). Filtered to the typed prefix only once the user has actually
    // EDITED the field (townEdited) — a prefilled/minted value that happens
    // to share no prefix with the shelf must never filter history to
    // nothing. listTowns() reads localStorage directly (untracked), so
    // shelfVersion is the explicit reactive dependency forgetRecent() bumps
    // on mutation.
    recentTowns() {
      this.shelfVersion; // eslint-disable-line no-unused-expressions
      const all = listTowns();
      if (!this.townEdited) return all.slice(0, 8);
      const typed = (this.townId || "").toLocaleLowerCase();
      const list = typed ? all.filter(t => t.id.startsWith(typed)) : all;
      return list.slice(0, 8);
    }
  },
  data() {
    return {
      language: window.navigator.userLanguage || window.navigator.language,
      // FT-1242: the field marks — see the import note.
      uiTown,
      uiScript,
      blood: BLOOD,
      bloodLogo,
      ontheLogo,
      ontheLockup,
      uiEnter,
      bloodLockup,
      bloodO,
      // Golem fork (2026-08-18, user diagnosis): the background renders
      // center/cover, so anything positioned in VIEWPORT pixels drifts
      // against the art as the window changes — the title was a moving
      // target. It now anchors in IMAGE coordinates (the art is trimmed to
      // 1642x900 as of the recentre — FT-anon 2026-08-19 — with the dial's
      // centre now AT the image centre; the tower band's centre sits at
      // x 807, shifted from the original art's x 837 by the same 30px the
      // trim took off the left edge): this holds the cover math.
      bgA: { x: 0, y: 0, s: 1 },
      // Golem fork: the app-wide PNG-font choice (titleFonts.js) — reactive,
      // persisted; the title click is the dev control.
      fontState,
      // 2026-08-19: which town this browser is waiting to see opened, held
      // the same way fontState is — a plain shared object that Vue makes
      // reactive by finding it here. It is written from the socket plugin's
      // boot parse, which runs before this component exists, so it cannot
      // live in the component.
      townGate,
      // FT-1241: the player-side password gate — the same shared-module
      // idiom as townGate above, and for the same reason: an invite-link
      // boot writes it before this component exists.
      enterKeyGate,
      enterPassTry: "", // what the player typed at the gate panel
      // FT-1241: the host panel's optional locks (set at claim)…
      hostEnterPass: "",
      hostOpenPass: "",
      // …and the refusal state when an existing town says no to the host
      // seat: {error, owned, openPasswordSet} from the platform, plus the
      // open password typed at the refusal prompt.
      hostRefusal: null,
      hostOpenTry: "",
      // Golem fork: the entry panels.
      mode: null, // null = doors | "host" | "join"
      townId: "",
      townDropdownOpen: false,
      townEdited: false, // true once the user has actually typed — gates the prefix filter
      forgetConfirm: null, // id awaiting a second click to confirm forgetting
      shelfVersion: 0, // bumped on shelf mutation to invalidate recentTowns
      scriptId: "",
      joinId: "",
      joinName: "",
      hostTowns: [],
      // FT-1038: the join panel's one-click shelf — top 3, any role. Same
      // idiom as hostTowns/joinTowns above it: a plain data array, refreshed
      // on panel open (not a computed — nothing here reads a reactive dep).
      recentEnterTowns: [],
      statuses: {},
      copied: false,
      statusTimer: null,
      // FT-847: owned towns.
      meta: {}, // id → server public meta {name, scriptId, ...}
      scriptTouched: false, // the user chose a script by hand this visit
      // Golem fork: the script grid (the widget itself is the shared
      // ScriptPicker component; only the card DATA lives here).
      vaultShelf: [], // recents-shelf entries snapshotted on openHost
      scriptMeta: {} // script id → {count, author} (lazy, best-effort)
    };
  },
  watch: {
    // Live-normalize the host field: junk chars become dashes, capped at 24 —
    // the id the socket joins IS the id on the screen. (Edge dashes survive
    // while typing; townIdClean applies the full trim.)
    townId(value) {
      const live = String(value || "")
        .toLocaleLowerCase()
        .replace(/[^a-z0-9_-]+/g, "-")
        .slice(0, 24);
      if (live !== value) this.townId = live;
      // FT-1241: a refusal speaks about ONE town — typing another name
      // retires it (and the password offered against it) rather than letting
      // a stale "not yours" hang over a town it never meant.
      if (this.hostRefusal) {
        this.hostRefusal = null;
        this.hostOpenTry = "";
      }
    },
    townIdClean() {
      this.syncAttached();
    },
    // (The picker cleans up its own document listeners on unmount now —
    // it is the shared ScriptPicker component.)
    // FT-854: the blood-O on "Open the town" promises a hotkey — live only
    // while the host panel is up, deaf while typing in a field.
    mode(v) {
      if (v === "host") document.addEventListener("keyup", this.onHostPanelKey);
      else document.removeEventListener("keyup", this.onHostPanelKey);
    }
  },
  mounted() {
    this.computeBgAnchor();
    window.addEventListener("resize", this.computeBgAnchor);
  },
  beforeDestroy() {
    clearInterval(this.statusTimer);
    document.removeEventListener("keyup", this.onHostPanelKey);
    window.removeEventListener("resize", this.computeBgAnchor);
  },
  methods: {
    /** The background's cover transform: scale + top-left offset.
     *  W/H are the recentred art's own dimensions (background-clocktower
     *  -centered.png) — the trim shaved 30px off the left and 41px off the
     *  bottom of the original 1672x941, so it must track this file, not the
     *  untrimmed original still in the tree. */
    computeBgAnchor() {
      const W = 1642,
        H = 900;
      const vw = window.innerWidth,
        vh = window.innerHeight;
      const s = Math.max(vw / W, vh / H);
      this.bgA = { x: (vw - W * s) / 2, y: (vh - H * s) / 2, s };
    },
    async mintChecked() {
      const placeholder = this.townId;
      const checked = await mintAvailableTownId();
      if (this.townId === placeholder) this.townId = checked;
    },
    /** Default the Town field: the shelf's newest entry (either role), or a
     *  freshly minted name when the shelf is empty (prior behavior). Always
     *  a PROGRAMMATIC set, so townEdited resets — the dropdown shows the
     *  full shelf until the user actually types. */
    prefillTownId() {
      this.townEdited = false;
      const shelf = listTowns();
      if (shelf.length) {
        this.townId = shelf[0].id;
        return;
      }
      this.townId = mintTownId(); // synchronous placeholder while checking
      this.mintChecked();
    },
    /** Real keystrokes only (v-model's programmatic sets don't fire native
     *  input events) — from here on the dropdown filters by what's typed. */
    onTownInput() {
      this.townEdited = true;
    },
    onTownFocus() {
      this.townDropdownOpen = true;
    },
    onTownBlur() {
      // delay so a click on a dropdown row registers before it disappears
      setTimeout(() => {
        this.townDropdownOpen = false;
      }, 150);
    },
    /** The caret: click (or refocus) opens the full list, same as focus. */
    openTownDropdown() {
      this.townDropdownOpen = true;
      if (this.$refs.townInput) this.$refs.townInput.focus();
    },
    pickRecent(t) {
      this.townId = t.id;
      this.townEdited = false;
      this.townDropdownOpen = false;
      this.forgetConfirm = null;
    },
    /** The dropdown's trailing row: mint a fresh name, same minter reroll
     *  uses, straight into the field. */
    async pickNewTown() {
      this.townEdited = false;
      this.townDropdownOpen = false;
      await this.reroll();
    },
    /** Forget a shelf entry (client-side only). An owned entry needs a
     *  second click — forgetting it discards the edit key for good. */
    forgetRecent(t) {
      if (t.editKey && this.forgetConfirm !== t.id) {
        this.forgetConfirm = t.id;
        return;
      }
      this.forgetConfirm = null;
      removeTown(t.id);
      this.hostTowns = this.sortOwnedFirst(listTowns("host"));
      this.recentEnterTowns = listTowns().slice(0, 3);
      this.shelfVersion++;
    },
    openHost() {
      this.mode = "host";
      this.hostTowns = this.sortOwnedFirst(listTowns("host"));
      this.scriptId = editionJSON.some(e => e.id === this.edition.id)
        ? this.edition.id
        : "__custom";
      this.scriptTouched = false;
      this.vaultShelf = getRecents().slice(0, 8);
      this.watchTowns();
      this.refreshMeta();
      this.prefillTownId();
    },
    openJoin() {
      this.mode = "join";
      this.recentEnterTowns = listTowns().slice(0, 3);
      // FT-1200: the remembered name leads; a signed-in account's name is
      // the fallback default (a default, not a cage — typing over it wins,
      // and what's typed is what gets remembered).
      this.joinName =
        localStorage.getItem("golem.playerName") ||
        ((this.$store.state.session.account || {}).name || "").trim();
      this.watchTowns();
      this.refreshMeta();
    },
    /** FT-847: owned towns lead the shelf; recency holds within each group.
     *  FT-1241: "owned" now also means the ACCOUNT owns it (server meta). */
    sortOwnedFirst(towns) {
      return towns
        .slice()
        .sort((a, b) => (this.isYours(b) ? 1 : 0) - (this.isYours(a) ? 1 : 0));
    },
    /** FT-1241: the YOURS chip's whole meaning — an edit key on the shelf,
     *  or true account ownership reported by the platform (`ownedByYou`,
     *  a boolean computed against THIS session; no account id crosses). */
    isYours(t) {
      if (t.editKey) return true;
      const m = this.meta[t.id];
      return !!(m && m.ownedByYou);
    },
    /** Best-effort batch meta: display names + attached scripts. */
    refreshMeta() {
      const ids = [
        ...new Set([...listTowns("host"), ...listTowns("player")].map(t => t.id))
      ];
      townMeta(ids).then(meta => {
        this.meta = meta;
        // re-list: townMeta cached fresh display names onto the shelf
        if (this.mode === "host")
          this.hostTowns = this.sortOwnedFirst(listTowns("host"));
        if (this.mode === "join")
          this.recentEnterTowns = listTowns().slice(0, 3);
        this.syncAttached();
      });
    },
    /** Keep the script select honest about an owned town's saved script. */
    syncAttached() {
      if (this.attachedScriptId) {
        if (!this.scriptTouched) this.scriptId = "__attached";
      } else if (this.scriptId === "__attached") {
        this.scriptId = editionJSON.some(e => e.id === this.edition.id)
          ? this.edition.id
          : "__custom";
      }
    },
    /** FT-854: O = Open the town, while the host panel is up. */
    onHostPanelKey(e) {
      const tag = e.target && e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.target && e.target.isContentEditable) return;
      if ((e.key === "o" || e.key === "O") && this.townIdClean) {
        this.confirmHost();
      }
    },
    // ── Golem fork: the script grid (widget = the shared ScriptPicker) ───
    pickScript(card) {
      this.scriptId = card.id;
      this.scriptTouched = true;
    },
    /** On picker open: best-effort vault metadata (role count + author) —
     *  blank until known. peekScript never touches the recents shelf. */
    peekVaultMeta() {
      const wanted = [
        ...this.vaultShelf.map(s => s.id),
        this.attachedScriptId
      ].filter(id => id && !this.scriptMeta[id]);
      wanted.forEach(id => {
        peekScript(id)
          .then(script => {
            const roles = Array.isArray(script.roles) ? script.roles : [];
            const meta = roles.find(r => r && r.id === "_meta") || {};
            this.$set(this.scriptMeta, id, {
              count: roles.filter(r => r && r.id !== "_meta").length,
              author: script.author || meta.author || ""
            });
          })
          .catch(() => {});
      });
    },
    /** "N roles · by <author>" once the lazy peek lands; blank until then. */
    vaultBlurb(id) {
      const m = this.scriptMeta[id];
      if (!m) return "";
      const parts = [`${m.count} role${m.count === 1 ? "" : "s"}`];
      if (m.author) parts.push(`by ${m.author}`);
      return parts.join(" · ");
    },
    /** Display name for a shelf row: cached/server name, else the id. */
    townLabel(t) {
      const m = this.meta[t.id];
      return (m && m.name) || t.name || t.id;
    },
    /** Poll the relay for awake/quiet while a panel is open. */
    watchTowns() {
      clearInterval(this.statusTimer);
      const refresh = () => {
        if (!this.mode) return clearInterval(this.statusTimer);
        const ids = [
          ...new Set(
            [...this.hostTowns, ...this.recentEnterTowns].map(t => t.id)
          )
        ];
        townStatuses(ids).then(statuses => {
          this.statuses = statuses;
        });
      };
      refresh();
      this.statusTimer = setInterval(refresh, 10000);
    },
    dotClass(id) {
      const s = this.statuses[id];
      if (!s) return "unknown";
      if (s.host) return "awake";
      return s.players ? "stirring" : "quiet";
    },
    statusLabel(id) {
      const parts = [];
      const s = this.statuses[id];
      if (s) {
        if (s.host) parts.push(`awake · ${s.players} in town`);
        else parts.push(s.players ? `${s.players} waiting` : "quiet");
      }
      // FT-1032: a town THIS browser dealt and has not ended carries its
      // day, read from the same local stashes the town itself restores from
      // (see the imports) — so a host scanning the shelf knows they are
      // walking back into Day 2, not into a fresh build.
      if (dealTimeFor(id)) {
        const log = loadLog(id);
        parts.push(`day ${Math.max((log && log.day) || 0, 1)}`);
      }
      return parts.join(" · ");
    },
    async reroll() {
      this.townId = await mintAvailableTownId();
      this.townEdited = false;
    },
    copyShare() {
      try {
        navigator.clipboard.writeText(this.shareLink);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 1500);
      } catch (e) {
        // clipboard is a bonus, never a blocker
      }
    },
    async confirmHost() {
      const id = this.townIdClean;
      if (!id) return;
      // FT-847: claiming is the default now — best-effort, never blocks
      // hosting. A "taken" name isn't an error: friend groups hosting each
      // other's towns is expected. FT-1241: a fresh claim records the
      // signed-in account as OWNER (the cookie rides by itself) and may set
      // the panel's optional passwords; an EXISTING town is no longer walked
      // into silently — the platform is asked whether this browser may take
      // the host seat, and a refusal stands the prompt up on the glass.
      let claimedNow = false;
      if (!editKeyFor(id)) {
        try {
          const res = await claimTown(id, id, undefined, {
            enterPassword: this.hostEnterPass.trim() || undefined,
            openPassword: this.account
              ? undefined
              : this.hostOpenPass.trim() || undefined,
          });
          claimedNow = !res.taken;
        } catch (e) {
          // unreachable / server error → host without keeping, quietly
        }
      }
      if (!claimedNow) {
        // Ours by key, someone else's, or the API was unreachable — one
        // question answers all three (openTown sends the held key and fails
        // open, so pre-FT-1241 towns and offline hosting are unchanged).
        const verdict = await openTown(
          id,
          this.hostOpenTry.trim() || undefined,
        );
        if (!verdict.ok) {
          this.hostRefusal = verdict;
          this.$nextTick(() => {
            if (this.$refs.openTryInput) this.$refs.openTryInput.focus();
          });
          return;
        }
      }
      this.hostRefusal = null;
      this.hostOpenTry = "";
      if (
        this.scriptId !== "__custom" &&
        this.scriptId !== "__attached" &&
        this.scriptId !== this.edition.id
      ) {
        const edition = editionJSON.find(e => e.id === this.scriptId);
        if (edition) this.$store.commit("setEdition", edition);
      }
      this.$store.commit("session/clearVoteHistory");
      this.$store.commit("session/setSpectator", false);
      this.$store.commit("session/setSessionId", id);
      this.copyShare();
      // "Custom / vault…" hands off to the script picker once the town is up —
      // but ONLY when the host actually chose it (user call 2026-08-19).
      //
      // `__custom` is also what `openHost` falls back to whenever the current
      // edition is not one of the built-ins, which is the state of an untouched
      // picker reading "Choose a script…". So opening a town without picking
      // anything used to drop the whole script workbench on top of the town
      // that had just opened. `scriptTouched` already distinguishes the two:
      // false means the picker was never used this visit.
      //
      // Nothing is lost by staying quiet — the build panel's own Script row is
      // right there, and picking from it is the same flow without a modal
      // covering the town first.
      if (this.scriptId === "__custom" && this.scriptTouched) {
        this.$store.commit("toggleModal", "edition");
      } else if (this.scriptId === "__attached" && this.attachedScriptId) {
        // FT-847: an owned town carries its script — load it through the same
        // vault path as a ?script= link (no re-attach round trip).
        const editionModal = this.$parent.$refs.edition;
        if (editionModal) editionModal.loadFromVault(this.attachedScriptId, false);
      } else if (!editionJSON.some(e => e.id === this.scriptId)) {
        // Golem fork: a vault script picked from the grid — the same load
        // path as a ?script= link; attach=true saves it onto an owned town
        // (best-effort inside, spectators/guests skipped).
        const editionModal = this.$parent.$refs.edition;
        if (editionModal) editionModal.loadFromVault(this.scriptId, true);
      }
    },
    /**
     * 2026-08-19: joining is now GATED on the town being open — a town with
     * no storyteller connected is waited for, not walked into. The name is
     * stored before the gate on purpose: the entry may happen minutes later,
     * from the waiting panel, with these fields long gone.
     *
     * A host is never gated. This panel joins as a player by construction, so
     * that can only be a storyteller taking a seat in a town they own — and
     * checking would refuse them at exactly the moment nobody is connected.
     */
    confirmJoin() {
      const id = this.joinIdClean;
      const name = this.joinName.trim();
      if (!id || !name) return;
      localStorage.setItem("golem.playerName", name);
      if (resolveTownRole(id) === "host") return this.enterAsPlayer(id);
      enterWhenOpen(id, this.enterAsPlayer);
    },
    /** FT-1038: the recent-shelf's one-click row. A name is usually already
     *  on file (openJoin seeds joinName from localStorage), so most clicks
     *  go straight through confirmJoin; the rare bare-browser case just
     *  fills the town field and hands focus to the name field rather than
     *  submitting a join nobody's named for. */
    enterRecent(t) {
      this.joinId = t.id;
      if (this.joinName.trim()) {
        this.confirmJoin();
      } else if (this.$refs.joinNameInput) {
        this.$refs.joinNameInput.focus();
      }
    },
    /** Take a seat in `id` as a player — now, or when the wait ends. */
    enterAsPlayer(id) {
      this.$store.commit("session/clearVoteHistory");
      this.$store.commit("session/setSpectator", true);
      // FT-1294: the entering player's `toggleGrimoire(false)` went with the
      // face-down state — the coins rest revealed for everyone now.
      this.$store.commit("session/setSessionId", id);
    },
    /** Leave the waiting panel for the entry screen. The gate's timer stops
     *  with it — nothing keeps polling behind a screen nobody is looking at,
     *  and a check already in flight can no longer pull them in (waitForTown
     *  re-reads the gate before acting on its answer). */
    stopWaiting() {
      stopWaitingForTown();
      this.mode = null;
    },
    /** FT-1241: the player typed a password at the town's door. Right → the
     *  gate module remembers it for this browser and the entry continues
     *  (straight in, or on to the waiting screen); wrong → the panel shows
     *  the server's refusal and asks again. */
    async offerPass() {
      const pass = this.enterPassTry.trim();
      if (!pass) return;
      const admitted = await offerEnterPass(pass);
      if (admitted) this.enterPassTry = "";
    },
    /** Leave the password gate for the entry screen — same shape as
     *  stopWaiting one door over. */
    stopKeyGate() {
      stopEnterPassGate();
      this.enterPassTry = "";
      this.mode = null;
    },
    /** Door/button drop-caps wear their OWN font (the Aa panel is the
     *  control; "follow" mirrors the title). Blood keeps the door-baked art
     *  + its pixel-tuned classes; sheet families render CAP_SHRINK smaller
     *  (the ornate letters overpower the labels at full size). */
    capKeyNow() {
      const k = resolvedCapKey();
      return k === "logo" ? "blood" : k;
    },
    capIsBaked() {
      return this.capKeyNow() === "blood";
    },
    capSrc(letter) {
      if (!this.capIsBaked()) {
        const g = glyphFrom(this.capKeyNow(), letter);
        if (g) return g.src;
      }
      return letter === "O" ? this.bloodO : this.blood[letter].src;
    },
    capClass(letter) {
      return this.capIsBaked()
        ? "blood-cap-" + letter.toLowerCase()
        : "font-cap";
    },
    /**
     * FT-anon (2026-08-19, user report — "the J is falling out of its box";
     * follow-up — "same with the H"): a lone drop-cap needs each letter
     * normalized against ITS OWN baseline, not the set's reference letter
     * (B) — B's baseline assumes every capital crops to the same height,
     * and it doesn't (J crops ~5% taller in red-97 and fell out of its
     * box). The fix (was inline here) is now `glyphCapStyleFrom` in
     * titleFonts.js — promoted FT-948 so the Keys panel, the app's other
     * lone-drop-cap surface, shares it instead of re-deriving its own.
     * Full reasoning lives on that export; glyphStyleFrom and its
     * title-word/"on the" callers are untouched, they need the opposite
     * (shared-B) behaviour.
     */
    capStyle(letter, scale = 1.09) {
      if (this.capIsBaked()) return null;
      return glyphCapStyleFrom(this.capKeyNow(), letter, scale);
    },
    /** Golem fork: the Create door opens the script editor straight to its
     *  Custom Script surface — same access pattern as the "Custom / vault…"
     *  script-grid card in confirmHost. */
    openCreate() {
      this.$store.commit("toggleModal", "edition");
      const editionModal = this.$parent.$refs.edition;
      if (editionModal) editionModal.isCustom = true;
    },
    /** FT-1146: the Records page (App mounts it on the `records` modal). No
     *  pick — the entry screen always lands on the whole-platform view. */
    openRecords() {
      this.$store.commit("setRecordsPick", null);
      this.$store.commit("toggleModal", "records");
    },
    press(key) {
      // The hotkey listener lives on the App root element (@keyup), so a
      // document-dispatched synthetic event never reaches it. Intro is App's
      // direct child — call the one handler with the same payload the key
      // would carry: still exactly one flow per verb.
      this.$parent.keyup({ key, ctrlKey: false, metaKey: false });
    }
  }
};
</script>

<style scoped lang="scss">
// FT-888: the clock face's disc — geometry, gate and material, shared with the
// night checklist and the build panel.
@import "../faceDisc.scss";

// Bloody (J. Fordyce, 1994, freeware) — the user's pick for the initials;
// Rubik Wet Paint (OFL) stays as the licensed fallback.
@font-face {
  font-family: Bloody;
  src: url("../assets/fonts/bloody.ttf");
  font-display: swap;
}
@font-face {
  font-family: WetPaint;
  src: url("../assets/fonts/rubikwetpaint.ttf");
  font-display: swap;
}

// The Intro root
// Golem fork: NO outer box — the doors and panels carry their own chrome,
// and the stack centers over the clock face (the dial sits at the cover-fit
// background's center, which is the viewport center #app already flexes to).
.intro {
  // FT-852: the intro is a full-screen COORDINATE LAYER now — same box the
  // background paints in — so everything inside anchors to the art, not to
  // a floating 460px overlay (the old drift) nor the browser viewport
  // (which mobile URL bars shrink — the fixed-position drift).
  text-align: center;
  position: absolute;
  inset: 0;
  font-size: 120%;
  z-index: 3;
  pointer-events: none;
  a {
    color: white;
  }

  > div {
    // full-size passthrough: absolute children (doors/panel/title) resolve
    // their percentages against THIS box when a transform/filter (the blur
    // transition) makes it their containing block — so it must span the
    // layer, not collapse to content height (the off-screen-doors bug).
    width: 100%;
    height: 100%;
  }

  // Golem fork: the page title — BLOOD (blood alphabet) / "on the" — pinned
  // to the sky band above the face; the dial's letters finish the name.
  // Fixed, so it holds the top-center regardless of the stack's height.
  .title {
    position: absolute;
    // left/top/font-size come from titleStyle — IMAGE-space anchoring
    // (the art is center/cover; viewport pixels drift against it)
    transform: translateX(-50%);
    text-align: center;
    pointer-events: none;
    z-index: 3;

    .blood-word {
      font-size: 1em;
      line-height: 1;
      white-space: nowrap;
      img.blood-lockup {
        width: 100%;
        height: auto;
        display: block;
        filter: drop-shadow(0 0.05em 0.08em rgba(0, 0, 0, 0.65));
      }
      // Golem fork: the lettering cycles on click (PNGs / gold / red) —
      // fixed row height so "On the" never shifts between states, and the
      // word alone takes the pointer (the .title wrapper stays inert).
      position: relative;
      height: 1.3em;
      pointer-events: auto;
      cursor: pointer;

      img {
        display: inline-block;
        margin: 0 0.025em;
        filter: drop-shadow(0 0.05em 0.08em rgba(0, 0, 0, 0.65));
      }

      // Golem fork (FT-853): state 1's official-style gold logo art — fills
      // the same fixed row height as the PNG-glyph state, so nothing shifts.
      img.blood-logo {
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        transform: translateX(-50%);
        height: 100%;
        width: auto;
        margin: 0;
        filter: drop-shadow(0 0.05em 0.08em rgba(0, 0, 0, 0.65));
      }
    }
  }

  // "on the" — its OWN image-space anchor (ontheStyle): centred on the
  // rosette ring, independent of the title block
  .on-the {
    position: absolute;
    transform: translate(-50%, -50%);
    z-index: 3;
    pointer-events: none;
    white-space: nowrap;
    img.onthe-lockup {
      height: 100%;
      width: auto;
      display: block;
      filter: drop-shadow(0 1px 3px black)
        drop-shadow(0 0 6px rgba(0, 0, 0, 0.6));
    }
  }

  // Golem fork: the three doors, in the editions' display face.
  .hint {
    margin: 6px 0 0;
    // legible over the raw art now that the outer box is gone
    text-shadow: 0 1px 3px black, 0 0 8px black;
    b { color: #c00; text-shadow: 0 0 4px black; }
  }

  ul.doors {
    list-style-type: none;
    display: flex;
    // Golem fork: a VERTICAL stack centered over the clock face — each door
    // keeps its own box; nothing wraps them.
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    // FT-852: sized in door face-pixels (--dfpx — face-proportional with a
    // phone cap, see App.vue). ANCHORED THE SAME WAY THE DIAL LETTERS ARE:
    // fixed to the app box at the hub point — NOT centered inside .intro,
    // whose 460px overlay box moves with the viewport (the drift that kept
    // eating the calibration). Recentred art (FT-anon 2026-08-19): the hub
    // sits at the image's own centre, so no baked offset is folded in here
    // any more. Nudge with --stack-trim, in image pixels, positive = down.
    //
    // FT-1190: THE HUB IS `--face-cx`/`--face-cy`, NOT A BARE 50%. Those two
    // are #app's published dial centre and they carry the paint's own
    // horizontal shift — `calc(50% + 7px + var(--bg-off-x, 0px))` (App.vue;
    // the 7px is FT-881's baked nudge, --bg-off-x is the face lab's live
    // scrub, a value persisted per browser profile in localStorage). Written
    // as a plain `left: 50%` this stack was the one thing on the dial that
    // did not move when the art did: 7px left of the face at every viewport
    // — a RAW pixel offset, so it never scales, and the smaller the window
    // the larger a share of the dial it eats (0.4% of a 1920px window, 1.9%
    // of a 375px pane) — and 0..40px further adrift for anyone whose profile
    // holds a lab scrub, which is why the same URL could look right in one
    // browser and shifted in another. Reading the variable means the doors
    // track the paint at every viewport and under any future change to it.
    --stack-trim: 0;
    position: absolute;
    pointer-events: auto;
    left: var(--face-cx);
    // -16.0 = the face's VISUAL center (the hands' boss, image y=434) —
    // measured against the art and confirmed by eye, not by formula alone.
    // (Was -36.5 against the original art's centre; the recentre trim
    // moved the reference point, not the boss itself, so the coefficient
    // shifted by +20.5 to match.)
    top: calc(var(--face-cy) + (-16.0 + var(--stack-trim)) * var(--fpx));
    transform: translate(-50%, -50%);
    margin: 0;
    gap: calc(5.5 * var(--dfpx));
    padding: calc(14 * var(--dfpx)) 0;
    // FT-1146 widened this 164 → 190 for the Records door: the plate is sized
    // to the widest word the stack holds, and "Records" beat "Scripts" (its
    // drop-cap R is nearly twice the S's) by 26 door-pixels.
    // FT-1159: that door left the stack, so the width it cost goes back. The
    // widest word is "Scripts" again and 164 is what it needs. The stack is
    // centred on the hub by translate(-50%), so narrowing moves nothing; only
    // the plates shrink, back to exactly the three doors' own measure.
    width: calc(164 * var(--dfpx));
    font-size: calc(53 * var(--dfpx));

    li {
      font-family: PiratesBay, sans-serif;
      letter-spacing: 1px;
      font-size: 100%;
      cursor: pointer;
      text-align: center;
      background: rgba(0, 0, 0, 0.9);
      border: 3px solid black;
      border-radius: 10px;
      box-shadow: 0 0 10px black;
      text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000,
        1px 1px 0 #000;
      transition: color 250ms;

      &:hover {
        color: red;
      }

      // user-calibrated 2026-08-17: Join (the middle door) reads largest;
      // Host and Create sit 25% smaller. Fixed heights (user spec: 70/50)
      // with line-height centering — the words center in their boxes while
      // the blood caps keep their baseline hang over the border.
      //
      // FT-1174 (user: "the text isn't shrinking to be within their boxes
      // correctly"). THE WORD AND THE PLATE WERE ON TWO DIFFERENT RULES.
      // Everything else in this stack is stated in door-pixels — the width
      // (164), the gap (5.5), the padding (14) and the TYPE ITSELF (53) —
      // so all of it grows and shrinks with the clock face. These four
      // heights were the one thing left in raw CSS pixels, which means the
      // words scaled and the plates they sit in did not. They agreed at one
      // window size and drifted apart at every other one:
      //
      //   (measured, claude_temp_test/2026-08-26-ft1174-doorprobe.mjs —
      //    ink box vs plate box, + = the word hanging outside the plate)
      //   1280x800   Host -5.6  Join -11.0  Scripts -10.0   fits
      //   1920x1080  Host +9.4  Join  +7.8  Scripts  -1.9   overflows
      //   2560x1440  Host +27.9 Join +32.5  Scripts +12.5   overflows badly
      //    390x844   Host -13.0 Join -20.0  Scripts -16.0   swims
      //
      // The failure is VERTICAL only: the width is already in door-pixels,
      // so the words have never come close to the side walls (worst measured
      // side clearance is 16.5px, on a 390px-wide phone).
      //
      // So the fix is to say the height in the same unit as everything else.
      // 1280x800 is the anchor — the size where the calibrated look was
      // still intact — and at that window --dfpx is 0.8889px, so the four
      // numbers convert x1.125: 70 -> 78.75, 64 -> 72, 50 -> 56.25, 44 ->
      // 49.5. NOTHING MOVES at 1280x800; every other size now holds the same
      // word-to-plate proportion instead of finding its own.
      //
      // And because the plate and the type are now one linear system, "it
      // fits" is proved once rather than per window: the clearance measured
      // in door-pixels is the same number at every size, which is exactly
      // what the old rule could not promise.
      box-sizing: border-box;
      white-space: nowrap;
      height: calc(78.75 * var(--dfpx));
      line-height: calc(72 * var(--dfpx));
      padding: 0 0.18em;
      &:nth-child(1),
      &:nth-child(3) {
        font-size: 75%;
        height: calc(56.25 * var(--dfpx));
        line-height: calc(49.5 * var(--dfpx));
      }
      // "Almanac" runs longer than the old "Create" — its own step down so
      // the word fits the same door on one line.
      &:nth-child(3) {
        font-size: 60%;
      }

      .key {
        font-family: Bloody, WetPaint, sans-serif;
        font-size: 105%;
        margin-right: 2px;
        color: #c00;
        text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000,
          1px 1px 0 #000;
        display: inline-block;

        // Golem fork (FT-846): the baked blood drop-cap; drips hang over the
        // door edge, above its border. Static per-letter sizing (from the
        // alphabet's trimmed metrics, baked here as plain CSS — FT-852).
        img {
          position: relative;
          z-index: 2;
        }
        .blood-cap-a { width: 0.805em; height: 0.927em; vertical-align: -0.124em; }
        .blood-cap-h { width: 0.712em; height: 0.927em; vertical-align: -0.127em; }
        .blood-cap-j { width: 0.681em; height: 0.936em; vertical-align: -0.136em; }
        .blood-cap-c { width: 0.687em; height: 0.942em; vertical-align: -0.139em; }
        // FT-1146: the Records door, same 1.09/360 em-per-trimmed-pixel
        // conversion as its four neighbours (metrics.json R: 243x289, 27
        // below). Written out rather than on one line like them so the file's
        // formatting warnings do not grow by this change.
        .blood-cap-r {
          width: 0.736em;
          height: 0.875em;
          vertical-align: -0.082em;
        }
      }
    }
  }

  // Golem fork: the Host / Join entry panels (replacing browser dialogs).
  // Each panel carries its OWN box now (the intro's outer box is gone) and
  // centers over the dial with the rest of the stack.
  .panel {
    // FT-852: hub-anchored exactly like the doors and the dial letters —
    // one coordinate system for everything sitting on the clock face.
    // Recentred art (FT-anon 2026-08-19): no baked offset folded in here;
    // top's -16.0 is the same hands'-boss adjustment the doors block uses.
    // FT-1190: the hub is `--face-cx`/`--face-cy` (see the doors block) — the
    // DISC form of this panel already reads them (faceDisc.scss centres every
    // disc on `var(--face-cx, 50%)`), so written as a bare 50% the rectangle
    // fallback sat 7px left of the disc it replaces. Both forms now land on
    // the same point, which is what "one coordinate system" above claimed.
    position: absolute;
    pointer-events: auto;
    left: var(--face-cx);
    top: calc(var(--face-cy) + -16.0 * var(--fpx));
    transform: translate(-50%, -50%);
    width: min(92vw, 420px);
    margin: 0;
    padding: 12px 14px;
    background: rgba(0, 0, 0, 0.9);
    border: 3px solid black;
    border-radius: 10px;
    box-shadow: 0 0 10px black;
    text-align: left;

    // FT-887: lives in .panel-head now (both the base rectangle and the
    // disc keep the wrapper — see the template), so this is a descendant
    // selector rather than the old direct child.
    .hint {
      text-align: center;
      margin-bottom: 8px;
    }

    ul.towns {
      list-style: none;
      margin: 0 0 10px;
      padding: 0;
      max-height: 30vh;
      overflow-y: auto;

      li {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 5px 8px;
        border-radius: 6px;
        cursor: pointer;
        border: 1px solid transparent;

        &:hover {
          color: red;
        }
        // LOUD, like the picker's picked card (user call 2026-08-17)
        &.picked {
          border-color: #a01414;
          background: rgba(160, 20, 20, 0.18);
          box-shadow: 0 0 10px rgba(210, 40, 40, 0.55),
            inset 0 0 16px rgba(160, 20, 20, 0.3);
          .name {
            text-shadow: 0 0 6px rgba(255, 60, 60, 0.7);
          }
        }
        .name {
          flex-grow: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          small.tid {
            opacity: 0.5;
            margin-left: 4px;
          }
        }
        small {
          opacity: 0.7;
          white-space: nowrap;
        }
        small.yours {
          opacity: 1;
          color: #c66;
          border: 1px solid #400;
          border-radius: 4px;
          padding: 0 5px;
          font-size: 65%;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .dot {
          flex-shrink: 0;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #333;
          box-shadow: 0 0 3px black;

          &.awake {
            background: #c00;
            box-shadow: 0 0 6px #c00;
          }
          &.stirring {
            background: #a60;
            box-shadow: 0 0 5px #a60;
          }
        }
      }
    }

    .field {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 8px;

      // Golem fork: labels are icon-only now (title attr carries the
      // tooltip) — the column shrinks to the glyph, fields widen to fill it.
      label {
        width: 26px;
        flex-shrink: 0;
        opacity: 0.8;
        text-align: center;

        /* FT-1242: the baked field marks sit in the same 26px label box the
           FA glyphs held, sized to the glyphs' own optical rank. The scroll
           is the dark engraved cut and needs a harder lift than the bone
           town to read at this size on this ground (judged live). */
        .field-mark {
          width: 18px;
          height: 18px;
          object-fit: contain;
          vertical-align: middle;
          filter: brightness(1.25) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
        }
        .field-mark-dark {
          filter: brightness(2.1) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
        }
      }

      // The invite-link glyph is a live copy control, so it invites the hand.
      label.link-copy {
        cursor: pointer;

        &:hover {
          opacity: 1;
        }
      }
      input,
      select {
        flex-grow: 1;
        min-width: 0;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        border: 2px solid black;
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 90%;
        outline: none;

        &:focus {
          border-color: #400;
        }
      }
      select option {
        background: black;
      }

      // (script-pick styles live in the shared ScriptPicker component)
      .tool {
        cursor: pointer;
        &:hover {
          color: red;
        }
        // "roll a new town name" drew at 12x17px
        @media (pointer: coarse) {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 40px;
          min-height: 40px;
        }
      }
      .town-input {
        position: relative;
        flex-grow: 1;
        min-width: 0;

        input {
          width: 100%;
          padding-right: 28px;
        }

        .caret {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          width: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0.7;
          font-size: 75%;
          // the door to the remembered-towns list, 26px wide inside a field
          // that is itself only 29px tall
          @media (pointer: coarse) {
            width: 40px;
          }

          &:hover,
          &.open {
            opacity: 1;
            color: red;
          }
        }

        ul.recents {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin: 4px 0 0;
          padding: 4px;
          list-style: none;
          background: rgba(0, 0, 0, 0.9);
          border: 2px solid #400;
          border-radius: 6px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 10;

          li {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 5px 8px;
            border-radius: 4px;
            font-size: 90%;

            &:hover {
              background: rgba(255, 0, 0, 0.15);
            }
            .name {
              flex-grow: 1;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              cursor: pointer;

              small.tid {
                opacity: 0.5;
                margin-left: 4px;
              }
              &:hover {
                color: red;
              }
            }
            small.yours {
              flex-shrink: 0;
              opacity: 1;
              color: #c66;
              border: 1px solid #400;
              border-radius: 4px;
              padding: 0 5px;
              font-size: 65%;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .forget {
              flex-shrink: 0;
              cursor: pointer;
              opacity: 0.6;
              padding: 0 3px;
              font-size: 85%;

              &:hover {
                opacity: 1;
                color: red;
              }
              &.sure {
                opacity: 1;
                color: #e88;
                font-size: 70%;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
            }
          }

          li.new-town {
            border-top: 1px solid rgba(255, 255, 255, 0.15);
            margin-top: 2px;
            padding-top: 7px;

            .name {
              cursor: pointer;
              opacity: 0.8;
              &:hover {
                opacity: 1;
                color: red;
              }
            }
          }
        }
      }
      .share {
        flex-grow: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        font-size: 80%;
        opacity: 0.9;

        // Copying the invite link is how anyone else gets into the town, and
        // the control is one line of 80%-size text — about 14px tall. It keeps
        // its type and gains a row.
        @media (pointer: coarse) {
          min-height: 40px;
        }

        .url {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        &:hover {
          color: red;
        }
      }
    }

    // ── FT-1241: the host-seat refusal, in the fields' own column ────────
    // A message in the server's honest vocabulary + the open-password field
    // when one exists. It rides where the lock fields stood (they hide while
    // it shows), so the panel never grows two competing stacks.
    .host-refusal {
      margin: 2px 0 8px;

      .refusal-why {
        margin: 0 0 8px;
        font-size: 85%;
        line-height: 1.35;
        color: #f77;
        text-shadow:
          0 1px 3px black,
          0 0 8px black;
      }
    }

    // ── FT-1038 (user call, 2026-08-21): "move the join town button to the
    //    middle of the disc and much bigger" ────────────────────────────
    // The band sits centred between two EQUAL caps (faceDisc.scss:
    // face-disc-head and face-disc-foot both claim `var(--fd-caph)`), so the
    // band's own vertical middle already IS the disc's. This panel stops
    // giving the bottom cap to a button: the shelf and the two fields stack
    // at their natural height, `.enter-dock` claims whatever the band has
    // left below them, and the door centres inside THAT box.
    //
    // MEASURED, not assumed: that lands the door's own centre a little
    // below the band's true middle — 83px at 1920x1080, less at smaller
    // discs where the (fixed-px) fields are a bigger share of a smaller
    // band. Pulling it to the exact midpoint would mean shrinking the shelf
    // or the fields until they clear less than half the band, which reads
    // worse than a few tens of pixels of vertical offset does: the door is
    // still the plate's dominant object, no longer riding outside the rim
    // the way it did at the foot cap, and "the middle of the disc" is a
    // composition call, not a coordinate.
    .panel-body.join-body {
      display: flex;
      flex-direction: column;

      // A touch tighter than the Host panel's own `.field` rows (unscoped,
      // so Host is untouched) — every px here is a px the door gets back.
      .field {
        margin-bottom: 5px;
      }
    }

    // The recent-shelf rows: same dot + status idiom `ul.towns` above
    // already carries, but a click here COMMITS (enterRecent) instead of
    // filling the field for review, so it stays its own list rather than a
    // reuse of `ul.towns`.
    ul.recent-shelf {
      list-style: none;
      margin: 0 0 6px;
      padding: 0;
      flex: 0 0 auto;

      li {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 8px;
        border-radius: 6px;
        cursor: pointer;
        border: 1px solid transparent;

        &:hover {
          border-color: #a01414;
          background: rgba(160, 20, 20, 0.15);
        }
        .name {
          flex-grow: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          &:hover {
            color: red;
          }
        }
        small {
          opacity: 0.7;
          white-space: nowrap;
        }
        .dot {
          flex-shrink: 0;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #333;
          box-shadow: 0 0 3px black;

          &.awake {
            background: #c00;
            box-shadow: 0 0 6px #c00;
          }
          &.stirring {
            background: #a60;
            box-shadow: 0 0 5px #a60;
          }
        }
      }
    }

    // THE ENTER DOOR'S DOCK. Centres the button in whatever room the shelf
    // and the fields above leave (see the offset note above .join-body).
    // NOT `flex: 1` here — this box is auto-height on the phone rectangle
    // (no `.panel-body` here carries a fixed size the way the disc's band
    // does), and a flex-grow child of an auto-height column has nowhere to
    // grow into. The disc gate further down this file is where it grows —
    // see the note there for what measuring the alternative found.
    .enter-dock {
      display: flex;
      // FT-1189: the door and its word are one object now, so the dock stacks
      // them and centres the pair — the door's own centring inside this box
      // (measured, see the note above) is unchanged, it simply centres a
      // taller stack.
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }

    // FT-1189: the door's own word. Quiet — the mark is the control and this
    // names it, so it takes the panel's small type rather than competing with
    // the field labels above it.
    .enter-label {
      font-size: 12px;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      opacity: 0.75;
      white-space: nowrap;
      transition: opacity 150ms;

      &.disabled {
        opacity: 0.3;
      }
    }

    button.enter-door {
      flex: 0 0 auto;
      // WIDTH DRIVES IT here: the rectangle's own width is always definite,
      // its height never is, so `aspect-ratio` derives the height from a
      // WIDTH percentage rather than the reverse. (The disc gate further
      // down flips this — there the band's HEIGHT is the tight, definite
      // dimension instead, and sizing off width there is what produced an
      // oval on the smaller discs: measured, not guessed.)
      width: min(60%, 150px);
      aspect-ratio: 1;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.7);
      border: 3px solid #400;
      border-radius: 50%;
      box-shadow: 0 0 14px black;
      cursor: pointer;

      &:hover {
        border-color: #a01414;
        box-shadow: 0 0 20px rgba(210, 40, 40, 0.6);
      }
      &.disabled {
        opacity: 0.4;
        cursor: not-allowed;
        border-color: black;
      }

      // ui-enter.png is 128x128 natively (checked — no larger source lives
      // in assets/ or design/); a door capped at 150px keeps the mark at or
      // under its own resolution rather than upscaling it.
      img.enter-mark {
        width: 56%;
        height: 56%;
        object-fit: contain;
      }
    }

    // FT-1012: the owned-note rule left with its element (FT-847 note retired).

    // ── 2026-08-19: NOT OPEN YET ────────────────────────────────────────
    // The waiting panel is the Join panel's own furniture rearranged — head
    // cap says the state, band explains it, foot is the way out — so it
    // inherits the disc geometry and the phone rectangle for free. Centred,
    // because there is no left-hand label column to line anything up against.
    // The three dots are the only thing on the screen that moves, and the
    // app's own animation kill-switch (#app.static) already stops them.
    &.wait {
      .panel-body {
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      // Everything in this panel sits on bare art — on the desktop disc the
      // clock face itself, which is the brightest thing in the app. So each
      // line carries the same black halo `.hint` already uses to stay legible
      // over the raw background; the Host and Join panels get away without it
      // because their content is dark input boxes.
      .wait-town {
        margin: 4px 0 10px;
        max-width: 100%;
        font-family: PiratesBay, sans-serif;
        font-size: 130%;
        color: #e11;
        text-shadow: 0 0 4px black, 0 1px 3px black, 0 0 12px black;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .wait-why,
      .wait-what {
        margin: 0 0 6px;
        font-size: 85%;
        line-height: 1.35;
        text-shadow: 0 1px 3px black, 0 0 8px black;
      }

      // FT-1241: the password ask reuses the panels' `.field` idiom inside
      // this centred band, which shrinks children to content — so the field
      // claims a readable row of its own.
      .field {
        width: min(100%, 260px);
        margin-top: 4px;
      }

      .wait-pulse {
        display: flex;
        gap: 8px;
        margin-top: 6px;

        span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #d02020;
          box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.55), 0 0 7px rgba(0, 0, 0, 0.9);
          animation: waitPulse 1.4s ease-in-out infinite;

          &:nth-child(2) {
            animation-delay: 0.2s;
          }
          &:nth-child(3) {
            animation-delay: 0.4s;
          }
        }
      }
    }

    // the way back sits at the panel's top-left (user call 2026-08-18), leaving
    // the bottom row to the action that matters — but IN FLOW, on a line of its
    // own. Floated over the corner it landed on the first remembered town, and
    // at a fingertip's 44px it covered that row's name outright (user report).
    .panel-back {
      display: inline-flex;
      align-items: center;
      justify-content: flex-start;
      margin: -2px 0 6px;
      cursor: pointer;
      opacity: 0.7;
      font-size: 120%;
      line-height: 1;
      &:hover {
        color: red;
        opacity: 1;
      }
      // A 13x16px arrow, and the only way back out of the Host and Join
      // panels. (The `.back` rule further down this file styles a class the
      // template no longer renders — this is the live one.)
      @media (pointer: coarse) {
        justify-content: center;
        min-width: 44px;
        min-height: 44px;
        margin: -6px 0 2px -8px;
      }
    }
    .acts {
      display: flex;
      align-items: center;
      // the primary action owns the bottom row: centred and wide
      justify-content: center;
      margin-top: 12px;

      button.confirm {
        min-width: 60%;
        display: flex;
        align-items: center;
        justify-content: center;
        // NO flex gap (user call 2026-08-19: "the O next to the P instead of
        // having space between them"). The drop-cap IS the label's first
        // letter — "O" + "pen the town" is one word broken across two boxes,
        // and a gap between flex items put 8px inside it. The doors get this
        // right with 2px of optical nudge and nothing more, so the button
        // matches them.
        //
        // The 8px was really the ENTER MARK's spacing — a mark and a word are
        // two things and do want air between them — so it moves onto the mark
        // itself below, where it belongs.
        gap: 0;
        > .key {
          margin-right: 2px;
        }
      }

      // FT-901 used to live here (the enter mark had no CSS at all and
      // dragged the button ~98px below the disc's rim). FT-1038 (2026-08-21,
      // user call) moves that whole door off this foot cap and into the
      // band's centre — `.enter-door` / `img.enter-mark`, under
      // `.join-body`, further down this file.

      // …and the button itself still crossed the rim at the tightest size once
      // the mark stopped dragging it: measured 1.028 of the plate's ellipse at
      // 1280x800 against 0.952 at 1920x1080, so it is a width problem at the
      // small end only. `min-width: 60%` of the plate is simply too wide down
      // there, because the button rides the bottom cap where the arc has
      // already closed in.
      //
      // Capped rather than re-positioned, and that is the same physics the
      // Start button's own note records: a narrower box brings its bottom
      // CORNERS toward the vertical axis, which is where the circle is
      // deepest. Moving it up would buy the same room and cost the placement
      // the user chose.
      // …and the button itself has to be able to HOLD its label. Measured on
      // this build: "Open the town" needs 148px of lettering and the box was
      // 129px, so the drop-cap hung 24.6px outside its own left border at
      // 1280x800 (3.5px at 1920x1080 — the same fault, just small enough to
      // read as a tight fit rather than a bug). The earlier cap was measured
      // on the JOIN panel, whose label is a single mark and never came close.
      //
      // So: size to the LABEL, with the 60% floor kept for the short one and
      // a ceiling that is the disc's, not a guess. 180px is where the bottom
      // corners reach the arc at the tightest plate — 0.98 of the ellipse at
      // 180, 1.00 at 190 — and 84% covers the plates between.
      button.confirm {
        width: max-content;
        max-width: min(84%, 180px);
      }

      .back {
        cursor: pointer;
        opacity: 0.7;
        font-size: 130%;
        line-height: 1;
        &:hover {
          color: red;
          opacity: 1;
        }
      }
      // Golem fork (FT-854): the primary action wears WORDS in the door
      // idiom — a blood drop-cap + the label, like the intro doors.
      button.confirm {
        font-size: 120%;
        font-family: inherit;
        line-height: 1.1;
        color: white;
        cursor: pointer;
        padding: 8px 22px;
        background: rgba(0, 0, 0, 0.7);
        border: 3px solid #400;
        border-radius: 10px;
        box-shadow: 0 0 10px black;

        .blood-cap-o {
          // 341x410, baseline 352, at the title's /370 conversion
          width: 0.922em;
          height: 1.108em;
          vertical-align: -0.157em;
        }

        &:hover {
          color: red;
        }
        &.disabled {
          opacity: 0.4;
          cursor: not-allowed;
          border-color: black;
        }
      }
    }

    // ── THE DISC (FT-887; shared in FT-888, desktop only) ───────────────
    //
    // The same object the night checklist and the build panel are: the
    // panel stops being a rectangle floating over the dial and becomes a
    // plate laid ON it. The geometry — radius off --face-r, cap fraction,
    // band chord, the gate, the material — is src/faceDisc.scss's, not
    // this file's. It used to be a COPY of NightSheet's numbers sitting
    // here (cap 0.21, half-width 0.8146, the same media query written out
    // again), which is exactly how a shape becomes three shapes.
    //
    // TWO CAPS, ONE BAND. The back arrow (+ the join panel's one-line
    // hint) rides the top cap; the primary button rides the bottom cap;
    // `.panel-body` — the town list and the fields — lives in the band
    // between them.
    //
    // NO overflow:hidden ON THE DISC, and that is this panel's own
    // departure from the checklist. The checklist's rows never open
    // anything outside themselves; this panel's town-name field and its
    // ScriptPicker both do (`ul.recents`, `.script-pick .grid` — both
    // `position: absolute`, both wider than the band). A circular clip
    // would cut them off along with everything else, so the ring here is
    // sized to hold ordinary content and a popup is left free to sit over
    // the rim the way it already sits over the rectangle's edge today —
    // asked for explicitly, and checked in the proof rig.
    // THE ENTRY PLATE, not the town one (2026-08-19). The disc's geometry is
    // now two sets and this is the only file that asks for the second: the
    // Host and Join panels sit on a lit dial, are read while the app is idle,
    // and the user dialled them to their own position, radius and width.
    // `face-disc-frame`'s default is the TOWN set (the checklist and the build
    // panel), so those two files are untouched and this one names its variant.
    // Every number in it lives in `$face-disc-geo-entry`, in src/faceDisc.scss.
    @include face-disc-gate {
      @include face-disc-frame($face-disc-geo-entry);
      overflow: visible;

      .panel-head {
        @include face-disc-head;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        padding: 0 0 4px;

        .panel-back {
          margin: 0;
        }
        .hint {
          margin: 4px 0 0;
        }
      }

      .panel-body {
        @include face-disc-band;
        padding: 0 6px;
        text-align: left;

        // the town list keeps its OWN internal scroll (unchanged — it
        // holds no popup, so clipping it costs nothing); it just stops
        // claiming most of the band the way 30vh did on a ~245px band.
        ul.towns {
          max-height: 96px;
        }

        // FT-1038: ONLY HERE does the door's dock get to fill the band's
        // leftover height — `@include face-disc-band` is what makes this
        // `.panel-body` a DEFINITE height in the first place. Outside the
        // gate (the phone rectangle) the body is auto-height, and a
        // `flex: 1 1 0` child of an auto-height column has nothing to grow
        // into: measured, not guessed — it collapsed the dock to 0 and the
        // door to 6px square. So the base rules below size the door off its
        // WIDTH (always definite — the rectangle's own width is), and this
        // gated override is what switches the door to sizing off its
        // HEIGHT instead, because inside the disc that's the dimension
        // actually under pressure.
        .enter-dock {
          flex: 1 1 0;
          min-height: 0;
        }
        button.enter-door {
          width: auto;
          height: 100%;
          max-width: min(calc(0.55 * var(--fd-rx, 220px)), 150px);
        }
      }

      // THE FOOT SITS 10px LOWER HERE, not the town plate's 14px. The
      // checklist's was measured against a 42px button; this one is 48px and
      // its own sweep landed here.
      //
      // THAT 10px USED TO BE PASSED FROM THIS LINE and now lives in
      // `$face-disc-geo-entry` beside the rest of this plate's numbers, which
      // is why the argument is gone: with the geometry split per surface,
      // holding half of the entry plate's description here and half in the map
      // is exactly the drift `src/faceDisc.scss` exists to end. The mixin still
      // takes an override for a surface that needs one.
      .acts {
        @include face-disc-foot;

        // "Open the town" wrapped to two lines at the rectangle's 120%/
        // 8px-22px sizing — measured against a 0.95r box (the same bound
        // the checklist's end-night button clears the arc at, at this same
        // foot offset) it needs to be smaller, not the box wider: past
        // 0.95r the button starts crossing the rim. The join panel's
        // icon-only button was never close to this bound and is untouched
        // by the size drop.
        button.confirm {
          font-size: 92%;
          padding: 7px 12px;
          min-width: 0;
          width: 100%;
          white-space: nowrap;
        }
      }
    }
  }

  a.redirect {
    display: block;
    text-decoration: none;
    position: absolute;
    top: 100%;
    margin-top: 2vh;
    padding: 10px;
    background: rgba(0, 0, 0, 0.5);
    border: 3px solid black;
    border-radius: 10px;

    &:hover {
      color: red;
    }
    img {
      width: 120px;
      display: block;
      margin: auto;
      margin-bottom: 1vh;
    }
  }

  // the waiting panel's three dots — the one moving thing on that screen
  @keyframes waitPulse {
    0%,
    100% {
      opacity: 0.25;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  img.logo {
    position: absolute;
    bottom: 100%;
    width: 25vh;
    max-width: 190px;
    margin-bottom: 2vh;
    border-radius: 50%;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.8);
    border: 3px solid black;
  }

  .footer {
    font-size: 60%;
    opacity: 0.75;
  }
}
</style>
